import React, { useEffect, useRef, useState } from 'react';
import { Range } from 'react-range';
import {
  ServiceActions,
  ServiceBadge,
  ServiceButton,
  ServiceCard,
  ServiceDangerButton,
  ServiceDivider,
  ServiceField,
  ServiceHeader,
  ServiceInfoLink,
  ServiceLogo,
  ServicePrice,
  ServicePrimaryButton,
  ServiceSliderRow,
  ServiceTitleBlock,
} from './ServiceComponent.styles';
import { BACKEND_BASE_URL } from '../../../../config';
import { removeExtraService, updateExtraService } from '../../../../features/purchases/purchasesActions';
import { confirmAlert, generalAlertError } from '../../../utils/alerts';
import axe from '../../../../services/axios-client/axiosClient';
import { downloadFile } from '../../../../services/axios-client/axeHelpers';

// ? CALCS
// ? total_price = 60
// ? raw_price_removed_base = total_price - 14 (Car Rescue 30)
// ? vat = total_price x 0.22 / 1.22
// ? client_earn = raw_price_removed_base - vat

// ? 60 - 14 = 46
// ? 60 x 0.22 / 1.22 = 10.82
// ? 46 - 10.82 = 35.18

const STEP = 1;
// Resource documents: Informazioni Precontrattuali
const CAR_RESCUE_DOC_ID = 56;
const EXTRA_RESCUE_DOC_ID = 57;
const IVA = 1.22;
// Rounded to cents: 14 * 1.22 is 17.079999... in floating point, which the
// floor below would turn into 17.07
const ASSISTENZA_STRADALE_MIN = Math.round(12 * IVA * 100) / 100;
const OTHER_MIN = Math.round(14 * IVA * 100) / 100;
const floorCents = (n) => Math.floor(n * 100 + 1e-6) / 100;

function ServiceComponent({
  rc_quotation, risk_quotations, getTotal, collectQuoteData, insuranceRequest,
}) {
  // Check if assistenza_stradale quote is selected
  const [isAssistenzaStradale, setIsAssistenzaStradale] = useState(false);

  const MIN = isAssistenzaStradale ? ASSISTENZA_STRADALE_MIN : OTHER_MIN;
  const MAX = ((getTotal() * 30) / 100) * IVA;

  // Support multiple extra services
  // Always use insurance_request_extra_services as array, but only allow one for now
  const extraServices = insuranceRequest?.insurance_request_extra_services || [];
  const normalizedExtraServices = Array.isArray(extraServices)
    ? extraServices.slice(0, 1)
    : extraServices
      ? [extraServices]
      : [];

  // State for new service being added
  const [val, setVal] = useState({ values: [MIN] });
  const [isServiceAdded, setIsServiceAdded] = useState(normalizedExtraServices.length > 0);
  const [services, setServices] = useState(normalizedExtraServices);
  // Raw text while typing, so a partial number (e.g. "5" on the way to "50")
  // can sit below MIN without ever reaching val, the slider or the submit
  const [inputText, setInputText] = useState(null);
  const valRef = useRef(null);
  valRef.current = val;

  // Update isAssistenzaStradale when rc_quotation or risk_quotations change
  useEffect(() => {
    if (rc_quotation.company_id === 2) {
      setIsAssistenzaStradale(rc_quotation.guarantees?.some(
        (quote) => quote.guarantees_slug === 'assistenza_stradale',
      ));
    } else {
      setIsAssistenzaStradale(risk_quotations?.some(
        (quote) => quote.quote_type === 'assistenza_stradale',
      ));
    }
  }, [rc_quotation, risk_quotations]);

  // Update val when isAssistenzaStradale changes
  useEffect(() => {
    setVal({ values: [isAssistenzaStradale ? ASSISTENZA_STRADALE_MIN : OTHER_MIN] });
  }, [isAssistenzaStradale]);

  useEffect(() => {
    setServices(normalizedExtraServices);
    setIsServiceAdded(normalizedExtraServices.length > 0);
  }, [insuranceRequest?.insurance_request_extra_services]);

  // Add a new extra service
  const submitData = async (skipCollect = false) => {
    if (rc_quotation.brokerage_amount > 0) {
      generalAlertError('Non puoi aggiungere il servizio se è già stato aggiunto un brokeraggio.');
      return;
    }

    const value = Math.min(Math.max(valRef.current.values[0], MIN), MAX);
    const basePrice = value / IVA;
    const data = {
      raw_value: value,
      base_price: Math.round(basePrice * 100) / 100,
      service_name: isAssistenzaStradale ? 'Extra Rescue 50' : 'Car Rescue 30',
      price_with_iva: Math.round(basePrice * IVA * 100) / 100,
      initial_service_price: isAssistenzaStradale ? 12 : 14,
    };

    // Only allow one service for now
    setServices([data]);
    setIsServiceAdded(true);
    // Optionally update backend here if needed
    if (skipCollect) {
      await updateExtraService(rc_quotation.request_id, data, () => { }, skipCollect);
    } else {
      await updateExtraService(
        rc_quotation.request_id,
        data,
        collectQuoteData,
        skipCollect,
      );
    }
  };

  useEffect(() => {
    if (isServiceAdded && services.length > 0) {
      const service = services[0];

      setVal({ values: [service.total_price] });
    }
  }, [isServiceAdded]);

  // Remove the added extra service
  const removeService = async () => {
    await removeExtraService(rc_quotation.request_id, services[0]?.id, collectQuoteData);
    setServices([]);
    setIsServiceAdded(false);
    setVal({ values: [MIN] });
  };

  const serviceName = isAssistenzaStradale ? 'Extra Rescue 50' : 'Car Rescue 30';

  const downloadPrecontractualInfo = async () => {
    const res = await axe.get(
      `/download-resource-document/${isAssistenzaStradale ? EXTRA_RESCUE_DOC_ID : CAR_RESCUE_DOC_ID}/0`,
      { responseType: 'blob' },
    );
    downloadFile(res.data);
  };

  const infoLink = (
    <ServiceInfoLink type="button" onClick={downloadPrecontractualInfo}>
      Informazioni Precontrattuali
      {' '}
      {serviceName}
    </ServiceInfoLink>
  );

  const logo = rc_quotation.company?.logo ? (
    <ServiceLogo>
      <img
        src={`${BACKEND_BASE_URL}/brands/logos/my-rescue-logo.png`}
        alt="My Rescue Logo"
      />
    </ServiceLogo>
  ) : null;

  if (isServiceAdded && services.length > 0) {
    // Only show the first service for now
    const service = services[0];
    return (
      <ServiceCard className="selected">
        <ServiceHeader>
          {logo}
          <ServiceTitleBlock>
            <ServiceBadge>Servizio non assicurativo</ServiceBadge>
            <h3>Servizio Assistenza Stradale</h3>
            <p>{service.service_name}</p>
            {infoLink}
          </ServiceTitleBlock>
          <ServicePrice>
            <span>Valore servizio:</span>
            <h2>
              &euro;
              {Number(service.total_price).toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </h2>
          </ServicePrice>
        </ServiceHeader>
        <ServiceDivider />
        <ServiceActions>
          <ServiceButton type="button" onClick={() => setIsServiceAdded(false)}>
            Modifica
          </ServiceButton>
          <ServiceDangerButton
            type="button"
            onClick={() => confirmAlert(
              'Sei sicuro di voler rimuovere il servizio?',
              () => removeService(),
            )}
          >
            Rimuovi
          </ServiceDangerButton>
        </ServiceActions>
      </ServiceCard>
    );
  }

  return (
    <ServiceCard>
      <ServiceHeader>
        {logo}
        <ServiceTitleBlock>
          {/* <ServiceBadge>Servizio non assicurativo</ServiceBadge> */}
          <h3>Servizio Assistenza Stradale</h3>
          <p>{serviceName}</p>
          {infoLink}
        </ServiceTitleBlock>
        <ServiceActions>
          {services.length > 0 && !isServiceAdded && (
            <ServiceDangerButton onClick={() => setIsServiceAdded(true)} type="button">
              Chiudi
            </ServiceDangerButton>
          )}
          <ServicePrimaryButton
            type="button"
            onClick={() => confirmAlert(
              'Sei sicuro di voler aggiungere il servizio? Se lo aggiungi, non potrai più aggiungere un brokeraggio.',
              () => submitData(),
            )}
          >
            {isServiceAdded || services.length > 0 ? 'Modifica' : 'Aggiungi'}
          </ServicePrimaryButton>
        </ServiceActions>
      </ServiceHeader>
      <ServiceDivider />
      <ServiceField>
        <label htmlFor="service-value">Inserisci Valore</label>
        <input
          id="service-value"
          type="number"
          placeholder="Inserisci il valore"
          value={inputText ?? val.values[0]}
          min={MIN}
          max={MAX}
          onChange={(e) => {
            setInputText(e.target.value);
            const inputVal = parseFloat(e.target.value);
            if (!Number.isNaN(inputVal) && inputVal >= MIN) {
              setVal({ values: [floorCents(Math.min(inputVal, MAX))] });
            }
          }}
          onBlur={(e) => {
            const inputVal = parseFloat(e.target.value) || MIN;
            const clampedVal = Math.min(Math.max(inputVal, MIN), MAX);
            setVal({
              values: [floorCents(clampedVal)],
            });
            setInputText(null);
          }}
        />
      </ServiceField>
      <ServiceSliderRow>
        <span>
          &euro;
          {MIN.toFixed(2)}
        </span>
        <div className="slider">
          <Range
            values={val.values}
            step={STEP}
            min={MIN}
            max={MAX}
            onChange={(values) => {
              setInputText(null);
              if (values[0] > MAX) {
                setVal({
                  values: [floorCents(MAX)],
                });
              } else {
                setVal({
                  values: [floorCents(values[0])],
                });
              }
            }}
            renderTrack={({ props, children }) => (
              <div
                onMouseDown={props.onMouseDown}
                onTouchStart={props.onTouchStart}
                style={{
                  ...props.style,
                  height: '32px',
                  display: 'flex',
                  width: '95%',
                  margin: '0 auto',
                }}
              >
                <div
                  ref={props.ref}
                  style={{
                    height: '8px',
                    width: '100%',
                    borderRadius: '140px',
                    alignSelf: 'center',
                    backgroundColor: '#DFF3EA',
                  }}
                >
                  {children}
                </div>
              </div>
            )}
            renderThumb={({ props }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: '24px',
                  width: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#00A651',
                  border: '3px solid #fff',
                  boxShadow: '0 1px 4px rgba(16, 24, 40, 0.25)',
                  outline: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-28px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    color: '#00A651',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '15px',
                    fontWeight: 600,
                    lineHeight: '20px',
                  }}
                >
                  &euro;
                  {' '}
                  {floorCents(val.values[0])}
                </div>
              </div>
            )}
          />
        </div>
        <span>
          &euro;
          {MAX.toFixed(2)}
        </span>
      </ServiceSliderRow>
    </ServiceCard>
  );
}

export default ServiceComponent;
